import * as React from "react";
import { Button, Dialog, Field, Textarea } from "@hp/ui";

export interface ReasonModalProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel: string;
  confirmVariant?: "primary" | "danger";
  placeholder?: string;
  submitting?: boolean;
  /** When true, the reason is a nice-to-have comment, not a blocking requirement - confirm works with it blank. */
  optional?: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * A real modal dialog for capturing a reason/comment before confirming a
 * status change - required (min length enforced) for "reject" and "defer",
 * optional (any length, including blank) for status moves that just want a
 * record of why, never a bare confirm() either way.
 */
export function ReasonModal({
  open,
  title,
  description,
  confirmLabel,
  confirmVariant = "primary",
  placeholder = "Explain the reason for this decision...",
  submitting = false,
  optional = false,
  onCancel,
  onConfirm,
}: ReasonModalProps) {
  const [reason, setReason] = React.useState("");
  const [touched, setTouched] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    if (open) {
      setReason("");
      setTouched(false);
      const id = window.setTimeout(() => textareaRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  const trimmed = reason.trim();
  const isValid = optional || trimmed.length >= 8;

  function handleConfirm() {
    setTouched(true);
    if (!isValid || submitting) return;
    onConfirm(trimmed);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
      title={title}
      description={description}
      titleId="reason-modal-title"
      size="sm"
      closeOnBackdrop={false}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" variant={confirmVariant} onClick={handleConfirm} disabled={submitting}>
            {submitting ? "Saving..." : confirmLabel}
          </Button>
        </>
      }
    >
      <Field
        label={optional ? "Comment (optional)" : "Reason"}
        htmlFor="reason-modal-textarea"
        required={!optional}
        error={touched && !isValid ? "Give at least 8 characters explaining the decision." : undefined}
        hint={
          !touched || isValid
            ? optional
              ? "Add a note for the record, or leave blank."
              : "This is recorded on the claim candidate for audit."
            : undefined
        }
      >
        <Textarea
          id="reason-modal-textarea"
          ref={textareaRef}
          rows={4}
          placeholder={placeholder}
          invalid={touched && !isValid}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          onBlur={() => setTouched(true)}
        />
      </Field>
    </Dialog>
  );
}
